import dynamic from 'next/dynamic'
import React, { FC, memo, ReactNode, useMemo } from 'react'

import { SharedFacetItemProps } from '@Components/Facet/Display/Items/ItemProps'
import { Checkbox } from '@Components/FormElements/UI/Checkbox'

import styles from './Checkbox.module.scss'

interface CheckboxItemProps extends SharedFacetItemProps {
  checked: boolean
  count: number
  disabled?: boolean
  onChange?: () => void
  text: ReactNode
}

const SVG_ICON_CSS = {
  width: '25px',
  height: '25px',
}
const SvgMap = {
  'patternsFacet:dotted': dynamic(() => import('@Assets/SVG/facets/Dotted')),
  'patternsFacet:plaid': dynamic(() => import('@Assets/SVG/facets/Plain')),
  'patternsFacet:patterned': dynamic(() => import('@Assets/SVG/facets/Patterned')),
  'patternsFacet:solid-colored': dynamic(() => import('@Assets/SVG/facets/Solid')),
  'patternsFacet:striped': dynamic(() => import('@Assets/SVG/facets/Striped')),
  'patternsFacet:camouflage': dynamic(() => import('@Assets/SVG/facets/Camouflage')),
  'patternsFacet:floral': dynamic(() => import('@Assets/SVG/facets/Floral')),
  'patternsFacet:geometric': dynamic(() => import('@Assets/SVG/facets/Geometric')),
  'patternsFacet:herringbone': dynamic(() => import('@Assets/SVG/facets/Herringbone')),
  'patternsFacet:paisley': dynamic(() => import('@Assets/SVG/facets/Paisley')),
  'patternsFacet:print': dynamic(() => import('@Assets/SVG/facets/Print')),
  'patternsFacet:tartan': dynamic(() => import('@Assets/SVG/facets/Tartan')),
  'patternsFacet:tropical': dynamic(() => import('@Assets/SVG/facets/Tropical')),
  'chainTypeFacet:anchor-chain': dynamic(() => import('@Assets/SVG/facets/AnchorChain')),
  'chainTypeFacet:ball-chain': dynamic(() => import('@Assets/SVG/facets/BallChain')),
  'chainTypeFacet:box-chain': dynamic(() => import('@Assets/SVG/facets/BoxChain')),
  'chainTypeFacet:belcher-chain': dynamic(() => import('@Assets/SVG/facets/BelcherChain')),
  'chainTypeFacet:braid-chain': dynamic(() => import('@Assets/SVG/facets/BraidChain')),
  'chainTypeFacet:cable-chain': dynamic(() => import('@Assets/SVG/facets/CableChain')),
  'chainTypeFacet:chain-link': dynamic(() => import('@Assets/SVG/facets/ChainLink')),
  'chainTypeFacet:curb-chain': dynamic(() => import('@Assets/SVG/facets/CurbChain')),
  'chainTypeFacet:figaro-chain': dynamic(() => import('@Assets/SVG/facets/FigaroChain')),
  'chainTypeFacet:foxtail-chain': dynamic(() => import('@Assets/SVG/facets/FoxtailChain')),
  'chainTypeFacet:herringbone-chain': dynamic(() => import('@Assets/SVG/facets/HerringboneChain')),
  'chainTypeFacet:paperclip-chain': dynamic(() => import('@Assets/SVG/facets/PaperclipChain')),
  'chainTypeFacet:prong-link': dynamic(() => import('@Assets/SVG/facets/ProngLink')),
  'chainTypeFacet:rope-chain': dynamic(() => import('@Assets/SVG/facets/RopeChain')),
  'chainTypeFacet:wheat-chain': dynamic(() => import('@Assets/SVG/facets/WheatChain')),
  'chainTypeFacet:snake-chain': dynamic(() => import('@Assets/SVG/facets/SnakeChain')),
}
export const CheckboxItem: FC<CheckboxItemProps> = memo(({
  checked,
  count,
  disabled,
  id,
  onChange,
  text,
}) => {
  const icon = useMemo(() => {
    const LAST_TWO_COLON_PARTS: RegExp = /^.*:([^:]+:[^:]+)$/
    const output = id.replace(LAST_TWO_COLON_PARTS, '$1')
    const Icon = SvgMap[output] || null
    if (!Icon) {
      return null
    }
    return <Icon key={output} style={SVG_ICON_CSS} />
  }, [id])
  return (
    <div className={styles.facetDisplayRoot}>
      <Checkbox
        checked={checked}
        disabled={disabled}
        id={id}
        label={`${text}`}
        name={id}
        onChange={onChange}
        secondaryLabel={`(${count})`}
      >
        {icon}
        {text}
      </Checkbox>
    </div>
  )
}, (prev, next) => prev.checked === next.checked && prev.count === next.count && prev.disabled === next.disabled)
